import { AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline";
import {
  CalendarIcon,
  CaretSortIcon,
  ChevronRightIcon,
} from "@radix-ui/react-icons";
import {
  ColumnDef,
  Row,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  UtilizationData,
  useGetUtilization,
} from "src/api/Analytics/utilization";
import { Button } from "src/components/ui/button";
import { Calendar } from "src/components/ui/calendar";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "src/components/ui/hover-card";
import { Loading } from "src/components/ui/loading";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/ui/select";
import { Switch } from "src/components/ui/switch";
import { convertMsToNatural } from "src/pages/timesheets/timesheets";

type Props = {};

export default function UtilizationAnalytics({}: Props) {
  const [startDate, setStartDate] = useState<string>(
    dayjs().subtract(10, "week").toISOString()
  );
  const [endDate, setEndDate] = useState<string>(
    dayjs().add(5, "week").toISOString()
  );
  const [aggregation, setAggregation] = useState<
    "daily" | "weekly" | "monthly" | "quarterly"
  >(() => {
    const savedAggregation = localStorage.getItem(
      `utilization_trend_aggregation`
    );
    if (
      savedAggregation &&
      (savedAggregation === "daily" ||
        savedAggregation === "weekly" ||
        savedAggregation === "monthly" ||
        savedAggregation === "quarterly")
    ) {
      return savedAggregation as "daily" | "weekly" | "monthly" | "quarterly";
    } else {
      return "weekly";
    }
  });

  useEffect(() => {
    localStorage.setItem(`utilization_trend_aggregation`, aggregation);
  }, [aggregation]);

  const [utilizationType, setUtilizationType] = useState<"billable" | "total">(
    () => {
      const savedUtilizationType = localStorage.getItem(
        `utilization_trend_utilization_type`
      );
      if (
        savedUtilizationType &&
        (savedUtilizationType === "billable" ||
          savedUtilizationType === "total")
      ) {
        return savedUtilizationType as "billable" | "total";
      } else {
        return "total";
      }
    }
  );

  useEffect(() => {
    localStorage.setItem(`utilization_trend_utilization_type`, utilizationType);
  }, [utilizationType]);

  const [isShowingHours, setIsShowingHours] = useState(() => {
    const savedIsShowingHours = localStorage.getItem(
      `utilization_trend_is_showing_hours`
    );
    if (savedIsShowingHours && savedIsShowingHours === "true") {
      return true;
    } else {
      return false;
    }
  });

  useEffect(() => {
    localStorage.setItem(
      `utilization_trend_is_showing_hours`,
      isShowingHours.toString()
    );
  }, [isShowingHours]);

  return (
    <div>
      <div className="flex h-12 items-center border-b border-gray-200/70 px-8 text-sm text-gray-800">
        <div className="flex items-center space-x-2 text-sm">
          <Link to={`/analytics`}>
            <div className="text-gray-800">{"Analytics"}</div>
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">
            {"Utilization Trend"}
          </div>
        </div>
      </div>
      <div className="flex h-16 items-center gap-2 border-b border-gray-200/70 px-8">
        {/* <Button variant={"secondary"}>Filter</Button> */}
        {/* <Display
          aggregation={aggregation}
          setAggregation={setAggregation}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          startDate={startDate}
          endDate={endDate}
        /> */}

        {/* <CalendarRange
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        /> */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"secondary"}
              className={"flex h-8 justify-between py-0 text-left font-normal"}
            >
              <div className="flex h-full items-center pr-2">
                <CalendarIcon className="mr-2 h-4 w-4" />
                <div>Start Date</div>
              </div>
              <div className="inline-flex h-full items-center border-l border-gray-200/70 pl-2">
                {dayjs(startDate).format("DD MMM, YYYY")}
              </div>
              <CaretSortIcon className="ml-2 h-4 w-4 text-black opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={startDate ? new Date(startDate) : undefined}
              onSelect={(date) => {
                if (date) {
                  setStartDate(date.toISOString());
                }
              }}
            />
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"secondary"}
              className={"flex h-8 justify-between py-0 text-left font-normal"}
            >
              <div className="flex h-full items-center pr-2">
                <CalendarIcon className="mr-2 h-4 w-4" />
                <div>End Date</div>
              </div>
              <div className="inline-flex h-full items-center border-l border-gray-200/70 pl-2">
                {dayjs(endDate).format("DD MMM, YYYY")}
              </div>
              <CaretSortIcon className="ml-2 h-4 w-4 text-black opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={endDate ? new Date(endDate) : undefined}
              onSelect={(date) => {
                if (date) {
                  setEndDate(date.toISOString());
                }
              }}
            />
          </PopoverContent>
        </Popover>
        <Select
          value={utilizationType}
          onValueChange={(value) =>
            setUtilizationType(value as "billable" | "total")
          }
        >
          <SelectTrigger className="h-8 w-44">
            <div className="flex h-8 items-center border-r border-gray-200/70 pr-2">
              Utilization
            </div>
            <div className="flex-1 pl-2 text-left">
              {utilizationType[0].toUpperCase() + utilizationType.slice(1)}
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="total">Total</SelectItem>
            <SelectItem value="billable">Billable</SelectItem>
          </SelectContent>
        </Select>
        <Display
          aggregation={aggregation}
          setAggregation={setAggregation}
          isShowingHours={isShowingHours}
          setIsShowingHours={setIsShowingHours}
        />
      </div>
      <div>
        <UtilizationTable
          startDate={startDate}
          endDate={endDate}
          aggregation={aggregation}
          utilizationType={utilizationType}
          isShowingHours={isShowingHours}
        />
      </div>
    </div>
  );
}

// function CalendarRange({
//   startDate,
//   endDate,
//   setStartDate,
//   setEndDate,
// }: {
//   startDate: string;
//   endDate: string;
//   setStartDate: (startDate: string) => void;
//   setEndDate: (endDate: string) => void;
// }) {
//   return (
//     <Popover>
//       <PopoverTrigger asChild>
//         <Button
//           variant={"secondary"}
//           className={"flex h-8 justify-between py-0 text-left font-normal"}
//         >
//           <div className="flex h-full items-center pr-2">
//             <CalendarIcon className="mr-2 h-4 w-4" />
//             <div>
//               {dayjs(startDate).format("ddd, DD MMM")} -{" "}
//               {dayjs(endDate).format("ddd, DD MMM")}
//             </div>
//           </div>
//           <CaretSortIcon className="h-4 w-4 text-black opacity-50" />
//           {/* <div className="inline-flex h-full items-center border-l border-gray-200/70 pl-2">
//             {dayjs(endDate).format("DD MMM, YYYY")}
//           </div>
//            */}
//         </Button>
//       </PopoverTrigger>
//       <PopoverContent className="flex w-auto items-center p-0" align="start">
//         <div className="border-r border-gray-200/70">A</div>
//         <div>B</div>
//       </PopoverContent>
//     </Popover>
//   );
// }

function Display({
  aggregation,
  setAggregation,
  isShowingHours,
  setIsShowingHours,
}: {
  aggregation: "daily" | "weekly" | "monthly" | "quarterly";
  setAggregation: (
    aggregation: "daily" | "weekly" | "monthly" | "quarterly"
  ) => void;
  isShowingHours: boolean;
  setIsShowingHours: (isShowingHours: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="secondary">
          <AdjustmentsHorizontalIcon className="mr-1.5 h-3.5 w-4" />
          Display
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="w-40 text-sm">View</div>
            <Select
              value={aggregation}
              onValueChange={(value) =>
                setAggregation(value as "weekly" | "monthly" | "quarterly")
              }
            >
              <SelectTrigger className="h-7 w-40">
                {/* <div className="flex h-8 items-center border-r border-gray-200/70 pr-2">
                  View
                </div> */}
                <div className="text-left">
                  {aggregation[0].toUpperCase() + aggregation.slice(1)}
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* <div className="flex items-center justify-between">
            <div className="w-40 text-sm">Utilization</div>
            
          </div> */}
          <div className="flex items-center justify-between">
            <div className="w-40 text-sm">Hours</div>
            <Switch
              checked={isShowingHours}
              onCheckedChange={(checked) => {
                setIsShowingHours(checked);
              }}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function getQuarter(date: Date) {
  const month = date.getMonth();
  return `Q${Math.floor((month + 3) / 3)} ${dayjs(date).format("YYYY")}`;
}

function UtilizationTable({
  startDate,
  endDate,
  aggregation,
  utilizationType,
  isShowingHours,
}: {
  startDate: string;
  endDate: string;
  aggregation: "weekly" | "monthly" | "quarterly" | "daily";
  utilizationType: "billable" | "total";
  isShowingHours: boolean;
}) {
  const { data: utilizationData } = useGetUtilization(
    startDate,
    endDate,
    aggregation
  );
  // console.log(utilizationData);

  const columns: ColumnDef<UtilizationData>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "User",
        cell: ({ row }) => <div className="w-60">{row.original.name}</div>,
      },
      ...(utilizationData && utilizationData.length > 0
        ? utilizationData[0].periods.map((period, index) => ({
            accessorKey: `periods[${period.period}].totalTrackedTime`,
            header: () => (
              <div
                className={clsx(
                  "px-6",
                  aggregation === "daily" ? "w-32" : "w-40"
                )}
              >
                {aggregation === "weekly"
                  ? `${dayjs(period.periodStartDate).format(
                      "MMM DD"
                    )} - ${dayjs(period.periodStartDate)
                      .add(6, "day")
                      .format("MMM DD")}`
                  : aggregation === "monthly"
                  ? dayjs(period.periodStartDate).format("MMM YYYY")
                  : aggregation === "quarterly"
                  ? getQuarter(dayjs(period.periodStartDate).toDate())
                  : aggregation === "daily"
                  ? dayjs(period.periodStartDate).format("MMM DD")
                  : ""}
              </div>
            ),
            cell: ({ row }: { row: Row<UtilizationData> }) => {
              const periodData = row.original.periods[index];
              const totalUtilization =
                (periodData.totalTrackedTime / periodData.totalCapacity) * 100;
              const billableUtilization =
                (periodData.totalBilledTrackedTime / periodData.totalCapacity) *
                100;
              const isWeekend =
                (dayjs(periodData.periodStartDate).day() === 0 ||
                  dayjs(periodData.periodStartDate).day() === 6) &&
                aggregation === "daily";
              const utilization =
                utilizationType === "total"
                  ? totalUtilization
                  : billableUtilization;
              if (isWeekend) {
                return (
                  <div
                    className={clsx(
                      "bg-gray-100/70",
                      isShowingHours ? "h-16" : "h-12",
                      aggregation === "daily" ? "w-32" : "w-40"
                    )}
                  />
                );
              }
              return (
                <HoverCard openDelay={50} closeDelay={0}>
                  <HoverCardTrigger>
                    <div
                      className={clsx(
                        "flex items-center px-6 ",
                        isShowingHours ? "h-16" : "h-12",
                        aggregation === "daily" ? "w-32" : "w-40",
                        utilization >= 0 &&
                          utilization < 30 &&
                          "bg-red-100/70 text-red-700",
                        utilization >= 30 &&
                          utilization < 70 &&
                          "bg-amber-100/70 text-amber-700",
                        utilization >= 70 && "bg-green-100/70 text-green-700"
                      )}
                    >
                      <div>
                        {`${utilization.toFixed(0)}%`}
                        {isShowingHours && (
                          <div className="mt-1 text-xs text-gray-500">
                            {convertMsToNatural(
                              utilizationType === "total"
                                ? periodData.totalTrackedTime
                                : periodData.totalBilledTrackedTime
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </HoverCardTrigger>
                  <HoverCardContent>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center space-x-2">
                        <div className="w-32">Capacity</div>
                        <div className="text-gray-700">
                          {convertMsToNatural(periodData.totalCapacity)}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-32">Total Tracked</div>
                        <div className="text-gray-700">
                          {convertMsToNatural(periodData.totalTrackedTime)}{" "}
                          <span
                            className={clsx(
                              "pl-1 font-medium",
                              totalUtilization >= 0 &&
                              totalUtilization < 30 &&
                                "text-red-600",
                                totalUtilization >= 30 &&
                                totalUtilization < 70 &&
                                "text-amber-500",
                                totalUtilization >= 70 && "text-green-600"
                            )}
                          >
                            [{totalUtilization.toFixed(0)}%]
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-32">Total Billable</div>
                        <div className="text-gray-700">
                          {convertMsToNatural(
                            periodData.totalBilledTrackedTime
                          )}{" "}
                          <span
                            className={clsx(
                              "pl-1 font-medium",
                              billableUtilization >= 0 &&
                                billableUtilization < 30 &&
                                "text-red-600",
                              billableUtilization >= 30 &&
                                billableUtilization < 70 &&
                                "text-amber-500",
                              billableUtilization >= 70 && "text-green-600"
                            )}
                          >
                            [{billableUtilization.toFixed(0)}
                            %]
                          </span>
                        </div>
                      </div>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              );
            },
          }))
        : []),
    ],
    [utilizationData, aggregation, utilizationType, isShowingHours]
  );

  const table = useReactTable({
    data: utilizationData ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!utilizationData) {
    return <Loading className="mx-auto mt-[10vh]" />;
  }

  if (utilizationData.length === 0) {
    return <div>No data</div>;
  }

  // define periods based on aggregation and start/end date

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-h-full max-w-full overflow-auto">
        <table
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
          className="min-w-[600px]"
        >
          <thead className="overflow-auto">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={
                      "sticky top-0 z-10 h-11 whitespace-nowrap border-b border-r border-gray-200 bg-gray-50 p-0 text-left text-xs font-normal uppercase tracking-wide text-gray-500 first:left-0 first:z-20 first:w-full first:pl-8"
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200/70 bg-white">
            <>
              <>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="group relative rounded-lg"
                    // onClick={() => {
                    //   onClick(row.original._id);
                    // }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={
                          "whitespace-nowrap border-r border-b border-gray-200 p-0 text-sm text-gray-500 first:sticky first:left-0 first:z-10 first:bg-white first:pr-6 first:font-medium first:text-gray-900 group-hover:bg-gray-50 first:sm:pl-8"
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            </>
          </tbody>
        </table>
      </div>
    </div>
  );
}
